/** How much of a message the toast shows before trailing off. */
export const MESSAGE_PREVIEW_LENGTH = 90;

export type MessageNotificationContext = {
  currentUserId: string;
  authorId: string;
  conversationId: string;
  /** The conversation currently on screen, if the user is on the messaging page. */
  openConversationId?: string;
};

export type MessageNotificationDecision = {
  incrementUnread: boolean;
  showToast: boolean;
};

/**
 * What an incoming message should trigger. Kept apart from any React or socket
 * plumbing so the rules can be read — and tested — on their own.
 */
export const decideMessageNotification = ({
  currentUserId,
  authorId,
  conversationId,
  openConversationId,
}: MessageNotificationContext): MessageNotificationDecision => {
  const isOwnMessage = authorId === currentUserId;
  const isConversationOnScreen = !!openConversationId && openConversationId === conversationId;

  if (isOwnMessage || isConversationOnScreen) {
    return { incrementUnread: false, showToast: false };
  }

  // Raised whether or not the tab is in the foreground: react-toastify pauses the
  // auto-close timer while the window is unfocused, so the toast waits for the user
  // instead of expiring behind their back.
  return { incrementUnread: true, showToast: true };
};

const MARKDOWN_PATTERNS: Array<[RegExp, string]> = [
  [/!\[([^\]]*)\]\([^)]*\)/g, '$1'], // images, keep the alt text
  [/\[([^\]]*)\]\([^)]*\)/g, '$1'], // links, keep the label
  [/^\s{0,3}#{1,6}\s+/gm, ''], // headings
  [/^\s{0,3}>\s?/gm, ''], // block quotes
  [/^\s{0,3}[-*+]\s+/gm, ''], // list bullets
  [/(\*\*|__)(.*?)\1/g, '$2'], // bold
  [/(\*|_)(.*?)\1/g, '$2'], // italic
  [/~~(.*?)~~/g, '$1'], // strikethrough
  [/`{1,3}([^`]*)`{1,3}/g, '$1'], // code
];

/**
 * One line of plain text taken from a message, for display in a toast.
 * Returns an empty string when the message carries nothing showable — the caller
 * decides what to say instead.
 */
export const buildMessagePreview = (content: string, maxLength: number = MESSAGE_PREVIEW_LENGTH): string => {
  const plain = MARKDOWN_PATTERNS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    content ?? '',
  )
    .replace(/\s+/g, ' ')
    .trim();

  if (plain.length <= maxLength) {
    return plain;
  }

  return `${plain.slice(0, maxLength)}…`;
};
